import Naluri from '~/common/api/naluri';
import {
  COMMON_GET_NEW_USERS_BEGIN,
  COMMON_GET_NEW_USERS_SUCCESS,
  COMMON_GET_NEW_USERS_FAILURE,
  COMMON_GET_NEW_USERS_DISMISS_ERROR,
  COMMON_UPDATE_NEW_USERS,
} from './constants';
import { sortWith, descend, prop, groupBy } from 'ramda';
import uniqBy from 'lodash/uniqBy';

// Rekit uses redux-thunk for async actions by default: https://github.com/gaearon/redux-thunk
// If you prefer redux-saga, you can use rekit-plugin-redux-saga: https://github.com/supnate/rekit-plugin-redux-saga
export function getNewUsers(
  pageNr = 1,
  { paid = true, vip = false, to_reply = false, sponsors = [] },
) {
  return (dispatch, getState) => {
    // optionally you can have getState as the second argument
    dispatch({
      type: COMMON_GET_NEW_USERS_BEGIN,
    });

    // Return a promise so that you could control UI flow without states in the store.
    // For example: after submit a form, you need to redirect the page to another when succeeds or show some errors message if fails.
    // It's hard to use state to manage it, but returning a promise allows you to easily achieve it.
    // e.g.: handleSubmit() { this.props.actions.submitForm(data).then(()=> {}).catch(() => {}); }
    const promise = new Promise((resolve, reject) => {
      // doRequest is a placeholder Promise. You should replace it with your own logic.
      // See the real-word example at:  https://github.com/supnate/rekit/blob/master/src/features/home/redux/fetchRedditReactjsList.js
      // args.error here is only for test coverage purpose.
      const page = pageNr;
      const params = {
        page: page,
        label: 'new_users',
        paid,
        vip,
        to_reply,
        sponsors,
      };

      const doRequest = Naluri.get(`/api/expert/channels/new_users`, params);
      doRequest.then(
        res => {
          dispatch({
            type: COMMON_GET_NEW_USERS_SUCCESS,
            data: res,
            pageNr: pageNr,
          });
          resolve(res);
        },
        // Use rejectHandler as the second argument so that render errors won't be caught.
        err => {
          dispatch({
            type: COMMON_GET_NEW_USERS_FAILURE,
            data: { error: err },
          });
          reject(err);
        },
      );
    });

    return promise;
  };
}

const filterUsers = (users, channels) => {
  const byUnread = sortWith([descend(prop('unreads_count'))]);
  const byStatus = sortWith([
    (a, b) => {
      const u1 = users.find(u => u.id === a.id);
      const u2 = users.find(u => u.id === b.id);
      const l1 = u1.status ? u1.status.length : 0;
      const l2 = u2.status ? u2.status.length : 0;

      return l2 - l1;
    },
  ]);

  const groupByStatusAndUnread = groupBy(a => {
    return a.unreads_count === 0 ? 'unread0' : a.status === 'online' ? 'online' : 'offline';
  });

  let onlineUsers = [];
  let offlineUsers = [];
  let unreadUsers = [];

  const groupedUsers = groupByStatusAndUnread(users);

  Object.keys(groupedUsers).forEach(key => {
    if (key === 'online') {
      onlineUsers = byUnread(groupedUsers[key]);
    } else if (key === 'offline') {
      offlineUsers = byUnread(groupedUsers[key]);
    } else {
      unreadUsers = byStatus(groupedUsers[key]);
    }
  });

  return [...onlineUsers, ...offlineUsers, ...unreadUsers];
};

const assignData = (users, channels) => {
  return users.map(user => {
    const channel = channels.find(c => c.id === user.cid);
    const last_message = channel
      ? channel.last_message
        ? channel.last_message.timestamp
        : Date.now()
      : Date.now();

    return {
      ...user,
      c_reply: channel ? channel.to_reply : false,
      c_type: channel ? channel.type : null,
      archived_status: channel ? channel.archived_status : false,
      last_message,
    };
  });
};

const filterDuplicateData = (originalData, iteratee) => {
  return uniqBy(originalData, iteratee);
};

// Async action saves request error by default, this method is used to dismiss the error info.
// If you don't want errors to be saved in Redux store, just ignore this method.
export function dismissGetNewUsersError() {
  return {
    type: COMMON_GET_NEW_USERS_DISMISS_ERROR,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case COMMON_GET_NEW_USERS_BEGIN:
      // Just after a request is sent
      return {
        ...state,
        getNewUsersPending: true,
        getNewUsersError: null,
      };

    case COMMON_GET_NEW_USERS_SUCCESS:
      // The request is success
      // Concatenate the users list if the request is asking for page number after 1
      let usersChannels;
      if (action.pageNr > 1) {
        usersChannels = state.newUsersChannels.concat(action.data.users);
      } else {
        usersChannels = action.data.users;
      }
      const channels = state.channels.concat(action.data.channels);

      const filteredUsersChannels = filterDuplicateData(usersChannels, 'cid');
      const filteredChannels = filterDuplicateData(channels, 'id');

      let sponsors = state.sponsors;
      if (action.data.sponsors) {
        action.data.sponsors.forEach(s => {
          if (!sponsors.includes(s) && s != null) sponsors.push(s);
        });
      }

      const assigned = assignData(filteredUsersChannels, filteredChannels);
      const filtered = filterUsers(assigned, filteredChannels);

      let pagination = state.newUsersPagination;
      if (action.data.pagination) {
        pagination = action.data.pagination;
      }

      return {
        ...state,
        newUsersChannels: filteredUsersChannels,
        channels: filteredChannels,
        sponsors: sponsors,
        newUsersPagination: pagination,
        newUsers: filtered,
        getNewUsersPending: false,
        getNewUsersError: null,
      };

    case COMMON_GET_NEW_USERS_FAILURE:
      // The request is failed
      return {
        ...state,
        getNewUsersPending: false,
        getNewUsersError: action.data.error,
      };

    case COMMON_GET_NEW_USERS_DISMISS_ERROR:
      // Dismiss the request failure error
      return {
        ...state,
        getNewUsersError: null,
      };

    case COMMON_UPDATE_NEW_USERS:
      return {
        ...state,
        newUsers: action.payload.newUsers,
        newUsersChannels: action.payload.newUsersChannels,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/features/common/redux/getNewUsers.js